<template>
  <div class="qa-panel">
    <div class="qa-panel-header">
      <h3>Open Discussion with AI</h3>
      <div class="qa-header-actions">
        <el-button
          v-if="qaItems.length"
          type="text"
          size="mini"
          @click="toggleAllExpand"
          class="toggle-all-btn"
        >
          <i :class="allExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          {{ allExpanded ? 'Collapse All' : 'Expand All' }}
        </el-button>
        <span class="qa-count" v-if="qaItems.length">{{ qaItems.length }} Q&A{{ qaItems.length > 1 ? 's' : '' }}</span>
      </div>
    </div>

    <div class="qa-list-section" ref="qaListSection">
      <div v-if="loading && !qaItems.length" class="qa-loading">
        <i class="el-icon-loading"></i>
        <span>Loading discussions...</span>
      </div>

      <div v-else-if="!qaItems.length" class="qa-empty">
        <div class="empty-icon">
          <i class="el-icon-chat-dot-round"></i>
        </div>
        <p>No discussions yet.</p>
        <p class="empty-hint">Be the first to ask a question!</p>
      </div>

      <transition-group v-else name="qa-card" tag="div" class="qa-cards">
        <div
          v-for="(qa, index) in qaItems"
          :key="qa.qa_id"
          :ref="'qa-' + qa.qa_id"
          class="qa-card"
          :class="{ 'expanded': expandedIds[qa.qa_id], 'is-new': qa._isNew }"
        >
          <div class="qa-card-question" @click="toggleExpand(qa.qa_id)">
            <div class="question-content">
              <i class="el-icon-chat-dot-round question-icon"></i>
              <span class="question-text">{{ qa.question }}</span>
            </div>
            <div class="question-meta">
              <span class="asker-name">{{ qa.user_name }}</span>
              <span class="ask-date">{{ formatDate(qa.created_at) }}</span>
              <i
                class="expand-arrow"
                :class="expandedIds[qa.qa_id] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
              ></i>
            </div>
          </div>
          <transition name="answer-slide">
            <div v-show="expandedIds[qa.qa_id]" class="qa-card-answer">
              <div class="answer-divider"></div>
              <div class="answer-body" v-html="renderMarkdown(qa.answer)"></div>
            </div>
          </transition>
        </div>
      </transition-group>
    </div>

    <div class="qa-input-section">
      <div class="input-wrapper">
        <el-input
          v-model="userQuestion"
          type="textarea"
          :rows="3"
          placeholder="Ask anything about this card."
          @keyup.enter.native="handleEnter"
          :disabled="submitting"
          resize="none"
        ></el-input>
        <el-button
          type="primary"
          :loading="submitting"
          :disabled="!userQuestion.trim()"
          @click="submitQuestion"
          class="send-btn"
        >
          <i v-if="!submitting" class="el-icon-s-promotion"></i>
          {{ submitting ? 'Thinking...' : 'Send' }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

export default {
  name: 'PostQAPanel',
  props: {
    postId: {
      type: String,
      default: ''
    },
    pageContent: {
      type: String,
      default: ''
    },
    cardId: {
      type: String,
      default: ''
    },
    cardContent: {
      type: String,
      default: ''
    },
    collectionTitle: {
      type: String,
      default: ''
    },
    userId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      qaItems: [],
      expandedIds: {},
      userQuestion: '',
      loading: false,
      submitting: false
    }
  },
  watch: {
    postId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.expandedIds = {}
          this.fetchQA()
        }
      }
    },
    cardId(newId) {
      if (newId) {
        this.expandedIds = {}
        this.fetchQA()
      }
    }
  },
  computed: {
    allExpanded() {
      return this.qaItems.length > 0 && this.qaItems.every(qa => this.expandedIds[qa.qa_id])
    }
  },
  methods: {
    async fetchQA() {
      if (!this.postId) return
      this.loading = true
      try {
        const params = { post_id: this.postId }
        if (this.cardId) {
          params.card_id = this.cardId
        }
        const response = await postRequest('getPostQA', params)
        if (response.data.code === 0 && response.data.data) {
          this.qaItems = response.data.data.reverse()
        } else {
          this.qaItems = []
        }
      } catch (e) {
        console.error('Failed to fetch Q&A:', e)
        this.qaItems = []
      } finally {
        this.loading = false
      }
    },

    toggleExpand(qaId) {
      this.$set(this.expandedIds, qaId, !this.expandedIds[qaId])
    },

    toggleAllExpand() {
      const allExpanded = this.qaItems.every(qa => this.expandedIds[qa.qa_id])
      const newState = {}
      this.qaItems.forEach(qa => { newState[qa.qa_id] = !allExpanded })
      this.expandedIds = newState
    },

    handleEnter(e) {
      if (e.shiftKey) return
      e.preventDefault()
      this.submitQuestion()
    },

    async submitQuestion() {
      const question = this.userQuestion.trim()
      if (!question || this.submitting) return

      this.submitting = true
      try {
        const params = {
          post_id: this.postId,
          user_id: this.userId,
          question: question
        }
        if (this.cardId) {
          params.card_id = this.cardId
        }
        if (this.cardContent) {
          params.card_content = this.cardContent
        }
        const response = await postRequest('askPostQuestion', params)

        if (response.data.code === 0 && response.data.data) {
          if (response.data.data.match) {
            this.scrollToAndExpand(response.data.data.matched_qa_id)
          } else if (response.data.data.new_qa) {
            const newQa = { ...response.data.data.new_qa, _isNew: true }
            this.qaItems.unshift(newQa)
            this.userQuestion = ''

            this.$nextTick(() => {
              this.$set(this.expandedIds, newQa.qa_id, true)
              const listSection = this.$refs.qaListSection
              if (listSection) {
                listSection.scrollTop = 0
              }
              setTimeout(() => {
                newQa._isNew = false
              }, 600)
            })
          }
        } else {
          this.$message.error(response.data.msg || 'Failed to get answer')
        }
      } catch (e) {
        console.error('Failed to ask question:', e)
        this.$message.error('Network error, please try again')
      } finally {
        this.submitting = false
      }
    },

    scrollToAndExpand(qaId) {
      this.$set(this.expandedIds, qaId, true)
      this.$nextTick(() => {
        const refKey = 'qa-' + qaId
        const el = this.$refs[refKey]
        if (el && el[0]) {
          el[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
          el[0].classList.add('highlight-pulse')
          setTimeout(() => el[0].classList.remove('highlight-pulse'), 1200)
        }
      })
    },

    renderMarkdown(content) {
      if (!content) return ''
      try {
        marked.setOptions({
          breaks: true,
          gfm: true
        })
        const rawHtml = marked(content)
        return DOMPurify.sanitize(rawHtml)
      } catch (e) {
        return content
      }
    },

    formatDate(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`

      const month = date.toLocaleString('en', { month: 'short' })
      const day = date.getDate()
      return date.getFullYear() === now.getFullYear()
        ? `${month} ${day}`
        : `${month} ${day}, ${date.getFullYear()}`
    }
  }
}
</script>

<style scoped>
.qa-panel {
  --primary: #1159af;
  --primary-dark: #0d4a96;
  --primary-light: #e8f0fb;
  --primary-rgb: 17, 89, 175;
  --text-primary: #1a2332;
  --text-secondary: #4a5568;
  --text-muted: #8a9ab5;
  --border-color: rgba(64, 158, 255, 0.1);
  --bg-page: #f4f7fb;
  --bg-card: #ffffff;

  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--bg-page);
  overflow: hidden;
}

.qa-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 14px;
  border-bottom: 1px solid var(--border-color);
  background: var(--primary);
  flex-shrink: 0;
}

.qa-panel-header h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.2px;
}

.qa-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toggle-all-btn {
  color: rgba(255, 255, 255, 0.85) !important;
  font-size: 12px;
  padding: 2px 6px;
}

.toggle-all-btn:hover {
  color: #fff !important;
}

.qa-count {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.22);
  padding: 2px 10px;
  border-radius: 12px;
  font-weight: 500;
}

/* Q&A List Area */
.qa-list-section {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 14px;
  scroll-behavior: smooth;
}

.qa-list-section::-webkit-scrollbar {
  width: 3px;
}

.qa-list-section::-webkit-scrollbar-thumb {
  background: rgba(var(--primary-rgb), 0.2);
  border-radius: 4px;
}

.qa-list-section::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--primary-rgb), 0.4);
}

/* Loading & Empty */
.qa-loading, .qa-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: var(--text-muted);
  gap: 8px;
}

.qa-loading i {
  font-size: 24px;
  color: var(--primary);
}

.empty-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--primary-light);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.empty-icon i {
  font-size: 26px;
  color: var(--primary);
  opacity: 0.6;
}

.qa-empty p {
  margin: 0;
  font-size: 13px;
  color: var(--text-secondary);
}

.qa-empty .empty-hint {
  font-size: 12px;
  color: var(--text-muted);
}

/* Q&A Cards */
.qa-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qa-card {
  background: var(--bg-card);
  border-radius: 10px;
  border: 1px solid #e8eef8;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.qa-card:hover {
  border-color: rgba(var(--primary-rgb), 0.25);
  box-shadow: 0 2px 10px rgba(var(--primary-rgb), 0.1);
}

.qa-card.expanded {
  border-color: rgba(var(--primary-rgb), 0.3);
  box-shadow: 0 4px 16px rgba(var(--primary-rgb), 0.12);
}

.qa-card.is-new {
  animation: cardAppear 0.45s cubic-bezier(0.4, 0, 0.2, 1);
}

.qa-card.highlight-pulse {
  animation: highlightPulse 1.2s ease;
}

@keyframes cardAppear {
  0% {
    opacity: 0;
    transform: translateY(-16px) scale(0.97);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes highlightPulse {
  0%, 100% { box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04); }
  50% { box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.25), 0 4px 16px rgba(var(--primary-rgb), 0.15); }
}

/* Question header */
.qa-card-question {
  padding: 12px 14px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}

.qa-card-question:hover {
  background: #f7faff;
}

.question-content {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 6px;
}

.question-icon {
  color: var(--primary);
  font-size: 14px;
  margin-top: 2px;
  flex-shrink: 0;
}

.question-text {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.5;
  word-break: break-word;
}

.question-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 22px;
}

.asker-name {
  font-size: 11px;
  color: var(--primary);
  font-weight: 500;
}

.ask-date {
  font-size: 11px;
  color: var(--text-muted);
}

.expand-arrow {
  margin-left: auto;
  font-size: 12px;
  color: var(--text-muted);
  transition: transform 0.25s, color 0.25s;
}

.qa-card.expanded .expand-arrow {
  color: var(--primary);
}

/* Answer section */
.answer-slide-enter-active,
.answer-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 500px;
  overflow: hidden;
}

.answer-slide-enter,
.answer-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.qa-card-answer {
  overflow: hidden;
}

.answer-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(var(--primary-rgb), 0.15), transparent);
  margin: 0 14px;
}

.answer-body {
  padding: 12px 14px 14px;
  font-size: 13px;
  line-height: 1.72;
  color: var(--text-secondary);
}

.answer-body :deep(p) {
  margin: 6px 0;
}

.answer-body :deep(ul),
.answer-body :deep(ol) {
  padding-left: 1.5em;
  margin: 6px 0;
}

.answer-body :deep(li) {
  margin: 3px 0;
}

.answer-body :deep(code) {
  background: var(--primary-light);
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
  color: var(--primary);
}

.answer-body :deep(pre) {
  background: #f4f8ff;
  border: 1px solid rgba(var(--primary-rgb), 0.1);
  border-radius: 8px;
  padding: 10px 12px;
  overflow-x: auto;
  margin: 8px 0;
}

.answer-body :deep(pre code) {
  background: transparent;
  color: #303133;
  padding: 0;
}

.answer-body :deep(blockquote) {
  border-left: 3px solid var(--primary);
  margin: 8px 0;
  padding: 6px 12px;
  background: var(--primary-light);
  color: var(--text-secondary);
  border-radius: 0 6px 6px 0;
}

.answer-body :deep(strong) {
  font-weight: 600;
  color: var(--text-primary);
}

/* Transition group animations */
.qa-card-enter-active {
  transition: all 0.45s cubic-bezier(0.4, 0, 0.2, 1);
}

.qa-card-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.qa-card-enter {
  opacity: 0;
  transform: translateY(-16px);
}

.qa-card-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

.qa-card-move {
  transition: transform 0.45s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Input Section */
.qa-input-section {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding: 14px;
  background: var(--bg-card);
  border-top: 1px solid var(--border-color);
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.input-wrapper :deep(.el-textarea__inner) {
  border-radius: 10px;
  border: 1.5px solid #dde6f4;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.5;
  transition: border-color 0.2s, box-shadow 0.2s;
  resize: none;
  background: #f9fbff;
}

.input-wrapper :deep(.el-textarea__inner):focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
  background: #fff;
}

.input-wrapper :deep(.el-textarea__inner)::placeholder {
  color: var(--text-muted);
}

.send-btn {
  align-self: flex-end;
  border-radius: 8px;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  background: var(--primary);
  border: none;
  transition: all 0.2s;
}

.send-btn:hover:not(:disabled) {
  background: var(--primary-dark) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(var(--primary-rgb), 0.4);
}

.send-btn:active:not(:disabled) {
  transform: translateY(0);
}

.send-btn:disabled {
  opacity: 0.45;
  background: #b8c5d6 !important;
}

.send-btn i {
  margin-right: 4px;
}
</style>
